/**
 * The app's version, one format everywhere (owner, 2 Nov 2026: "an investor must be
 * able to tell me their version by looking at it"; docs/mobile-app.md, "Versions"):
 *
 *   Android / iPhone app:  "Version 1.0.4 (build 4) · screens 02 Nov 18:20"
 *   website:               "Web · 02 Nov 18:20 (c1eb449)"
 *
 * 1.0.<build> is the native app (Android versionName, iOS CFBundleShortVersionString;
 * both from web/app-native.json), <build> its build number. The screens are the
 * live-update bundle the app runs, named by the build's stamp
 * "2026.11.02-1820-c1eb449" (IST date and time, git commit), shown short.
 *
 * Shown in Account -> About this app, the user menu's footer and the sign-in page,
 * and reported to the server (POST /me/app-device) at sign-in and at each start so
 * Admin -> Users shows it. The phone's own details come from src/native/version.ts.
 */
import { useSyncExternalStore } from "react";

import { api } from "@/api/client";
import { getSession, isViewTab, subscribe } from "@/api/session";

declare const __WEB_BUILD__: string | undefined;

/** This build's stamp (vite.config.ts); "local" in tests. */
export const WEB_BUILD: string = typeof __WEB_BUILD__ === "string" ? __WEB_BUILD__ : "local";

const STAMP = /^(\d{4})\.(\d{2})\.(\d{2})-(\d{2})(\d{2})-([0-9A-Za-z]+)$/;
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "2026.11.02-1820-c1eb449" -> "02 Nov 18:20" (null when it isn't a stamp). */
export function shortStamp(stamp: string | null | undefined): string | null {
  const m = STAMP.exec(stamp ?? "");
  const month = m ? MONTHS[Number(m[2]) - 1] : undefined;
  return m && month ? `${m[3]} ${month} ${m[4]}:${m[5]}` : null;
}

export function stampSha(stamp: string | null | undefined): string | null {
  return STAMP.exec(stamp ?? "")?.[6] ?? null;
}

export type AppPlatform = "android" | "ios" | "web";
export const PLATFORM_LABEL: Record<AppPlatform, string> = { android: "Android", ios: "iPhone", web: "Web" };

export interface VersionInfo {
  platform: AppPlatform;
  /** Native app version, "1.0.4" (null on the website). */
  appVersion: string | null;
  build: number | null;
  /** The screens running now (the build's stamp). */
  screens: string;
  /** A newer bundle downloaded, switched to the next time the app restarts. */
  pending: string | null;
  /** The newest screens need this native build (the app is older): install the new app. */
  needBuild: number | null;
}

const WEB: VersionInfo = { platform: "web", appVersion: null, build: null, screens: WEB_BUILD, pending: null, needBuild: null };

/** "Version 1.0.4 (build 4) · screens 02 Nov 18:20" / "Web · 02 Nov 18:20 (c1eb449)". */
export function versionLine(v: VersionInfo): string {
  const when = shortStamp(v.screens) ?? v.screens;
  if (v.platform === "web") {
    const sha = stampSha(v.screens);
    return `Web · ${when}${sha ? ` (${sha})` : ""}`;
  }
  return `Version ${v.appVersion ?? "?"}${v.build != null ? ` (build ${v.build})` : ""} · screens ${when}`;
}

/** The user menu's footer: "v1.0.4 · 02 Nov 18:20" / "Web · 02 Nov 18:20". */
export function shortLine(v: VersionInfo): string {
  const when = shortStamp(v.screens) ?? v.screens;
  return v.platform === "web" ? `Web · ${when}` : `v${v.appVersion ?? "?"} · ${when}`;
}

/** What "Copy version" puts on the clipboard: the platform and every full id. */
export function copyText(v: VersionInfo): string {
  const parts = [`${PLATFORM_LABEL[v.platform]} · ${versionLine(v)}`, `screens ${v.screens}`];
  if (v.pending) parts.push(`update ready: ${v.pending}`);
  return parts.join(" · ");
}

// ------------------------------------------------------------------ the store

let info: VersionInfo = WEB;
const listeners = new Set<() => void>();

/** src/native/version.ts fills in the phone's details (and updates them). */
export function setVersionInfo(next: Partial<VersionInfo>): void {
  info = { ...info, ...next };
  for (const fn of listeners) fn();
}

export function getVersionInfo(): VersionInfo {
  return info;
}

function listen(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function useAppVersion(): VersionInfo {
  return useSyncExternalStore(listen, getVersionInfo, getVersionInfo);
}

export function resetVersionForTests(next: Partial<VersionInfo> = {}): void {
  info = { ...WEB, ...next };
  for (const fn of listeners) fn();
}

// --------------------------------------------------------- telling the server

const INSTALL = "rai.install-id";

function installId(): string | null {
  try {
    let id = localStorage.getItem(INSTALL);
    if (!id || !/^[A-Za-z0-9-]{8,64}$/.test(id)) {
      id = crypto.randomUUID();
      localStorage.setItem(INSTALL, id);
    }
    return id;
  } catch {
    return null; // storage blocked: nothing to key the row on
  }
}

let lastSent = "";

/** Report this install's version once per start and after each sign-in. */
export async function reportVersion(): Promise<void> {
  if (getSession().status !== "signed-in" || isViewTab()) return;
  const id = installId();
  if (!id) return;
  const v = getVersionInfo();
  const body = {
    install_id: id,
    platform: v.platform,
    app_version: v.appVersion,
    build: v.build,
    screens: v.screens,
    pending: v.pending,
  };
  const key = `${getSession().user?.id ?? ""}|${JSON.stringify(body)}`;
  if (key === lastSent) return;
  lastSent = key;
  try {
    await api.post("/me/app-device", { body });
  } catch {
    lastSent = ""; // offline: the next start tries again
  }
}

let started = false;

export function startVersionReport(): void {
  if (started) return;
  started = true;
  void reportVersion();
  subscribe(() => void reportVersion());
  listen(() => void reportVersion());
}
